<template>
  <form class="d-grid gap-3 mt-5">
    <template v-if="errorMessage">
      <p v-if="errorMessageIsHtml" class="text-danger mb-4" v-html="errorMessage" />
      <p v-else class="text-danger mb-4">
        {{ errorMessage }}
      </p>
    </template>

    <text-input ref="emailField" :label="i18n.t('activerecord.attributes.user.email')" :value="email"
                input-name="user[email]" input-id="user-email-field" input-type="email" autocomplete="email"
                :required="true" @changed="emailChanged" />

    <password-field input-name="user[password]" :value="password" :required="true" @input="passwordChanged" />

    <checkbox-input input-name="user[remember_me]" :initial-value="rememberMe" checkbox-id="user-remember-me"
                    :label="i18n.t('member.account.authentication_modal.remember_me')"
                    @change="rememberMeChanged" />

    <submit-button :label="submitButtonText" class="submit-button" @click="submit" />

    <a v-if="enableForgotPassword" class="text-center mt-2 mb-4" :href="forgotPasswordUrl">
      {{ i18n.t('users.shared.login.forgot_password') }}
    </a>
  </form>
</template>

<script>
import ajax from '../helpers/ajax'
import CheckboxInput from '../components/inputs/checkbox-input'
import PasswordField from '../components/inputs/password-field.vue'
import SubmitButton from '../components/buttons/submit-button'
import TextInput from '../components/inputs/text-input'

export default {
  components: { CheckboxInput, PasswordField , SubmitButton, TextInput},

  props: {
    enableForgotPassword: {
      type: Boolean,
      required: false,
      default: true
    },

    forgotPasswordUrl: {
      type: String,
      required: false
    },

    organisationId: {
      type: Number,
      required: true
    },

    signInPath: {
      type: String,
      required: true
    },

    submitButtonText: {
      type: String,
      required: true
    }
  },

  emits: [
    'authenticated',
    'authenticity-token-updated',
    'mfa-setup-required',
    'mfa-token-required',
    'missing-info-required'
  ],

  data() {
    return {
      email: null,
      errorMessage: null,
      errorMessageIsHtml: false,
      password: null,
      rememberMe: false
    }
  },

  mounted() {
    this.$refs.emailField.focus()
  },

  methods: {
    emailChanged(value) {
      this.email = value
    },

    passwordChanged(value) {
      this.password = value
    },

    rememberMeChanged(value) {
      this.rememberMe = value
    },

    setErrorMessage(error) {
      switch (typeof error) {
      case 'string':
        this.errorMessage = error
        this.errorMessageIsHtml = false
        break
      case 'object':
        this.errorMessage = error.message
        this.errorMessageIsHtml = error.html_safe
        break
      default:
        throw 'invalid error message'
      }
    },

    submit() {
      ajax.post({
        url: this.signInPath,
        data: {
          user: {
            email: this.email,
            organisation_id: this.organisationId,
            password: this.password,
            remember_me: this.rememberMe
          }
        },
        throwOnFailure: true,
        successHandler: (response) => {
          if (response.success) {
            this.errorMessage = null

            if (response.authenticityToken) {
              this.$emit('authenticity-token-updated', response.authenticityToken)
            }

            if (response.requiresMfaSetup) {
              this.$emit('mfa-setup-required', {
                enableMfaPath: response.enableMfaPath
              })
            }
            else if (response.requiresMfaToken) {
              this.$emit('mfa-token-required', response.mfaParameters)
            } else if (response.requiresMissingInfo) {
              this.$emit('missing-info-required', {
                requiredFields: response.requiredFields,
                additionalFieldConfigs: response.additionalFieldConfigs,
                euDataProcessingConsentData: response.euDataProcessingConsentData,
                phoneNumberLabel: response.phoneNumberLabel,
                postcodeLabel: response.postcodeLabel
              })
            } else {
              this.$emit('authenticated', {
                afterSignInRedirect: response.afterSignInRedirect,
                userMenu: response.userMenu
              })
            }
          }
        }
      }).catch(({ error }) => {
        // We'll get here if the sign-in attempt received a non-200 (e.g. 401) response.
        this.setErrorMessage(error)

        this.email = ''
        this.password = ''
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@use "~common_styles/breakpoints" as breakpoints;

::v-deep(input) {
  &[type=text], &[type=password], &[type=email] {
    height: 58px;

    @include breakpoints.screen-mobile-only {
      height: 50px;
    }
  }
}

.submit-button {
  text-transform: none;
  height: 56px;
  border-radius: 8px;

  @include breakpoints.screen-mobile-only {
    height: 50px;
  }
}
</style>
